import React, { useState, useMemo } from "react";
import MultiSelectDropdown from "@/components/common-components/filter options/MultiSelectDropdown";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

const useDebouncedValue = (value, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  React.useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

export default function FilterDropdown({ label, value, onChange, options = [], multi = false, triggerClassName = '', contentClassName = '', enableSearch = true, ...props }) {
  // All hooks must be called unconditionally at the top level
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const inputRef = React.useRef(null);
  const debouncedSearch = useDebouncedValue(searchQuery, 300);

  // Normalize options to {value, label} format
  const normalizedOptions = useMemo(() => {
    if (!Array.isArray(options)) return [];
    return options.map(opt => {
      if (typeof opt === "string") {
        return { value: opt, label: opt };
      }
      return opt;
    });
  }, [options]);

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!enableSearch || !debouncedSearch) return normalizedOptions;
    return normalizedOptions.filter((opt) =>
      opt.label.toLowerCase().includes(debouncedSearch.toLowerCase())
    );
  }, [debouncedSearch, normalizedOptions, enableSearch]);

  // Get display value
  const displayValue = useMemo(() => {
    if (!value) return `Select ${label}`;
    const option = normalizedOptions.find(opt => opt.value === value);
    return option?.label || value;
  }, [value, normalizedOptions, label]);

  const isPlaceholder = !value;

  // Handle option selection
  const handleSelect = (optValue) => {
    if (value === optValue) {
      // Deselect if clicking the same option
      onChange("");
    } else {
      onChange(optValue);
    }
  };

  // Clear selection
  const handleClear = (e) => {
    e.stopPropagation();
    onChange("");
  };

  React.useEffect(() => {
    if (open && enableSearch && inputRef.current) {
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [open, enableSearch]);

  React.useEffect(() => {
    if (!open) {
      setSearchQuery("");
    }
  }, [open]);

  // Early return after all hooks are called
  if (multi) {
    return (
      <MultiSelectDropdown
        label={label}
        options={options}
        value={Array.isArray(value) ? value : value ? [value] : []}
        onChange={onChange}
        triggerClassName={triggerClassName}
        contentClassName={contentClassName}
        {...props}
      />
    );
  }

  return (
    <div className="flex flex-col min-w-[200px]">
      {label && (
      <label className="text-sm font-medium text-foreground leading-none mb-1">{label}</label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn("justify-between min-w-[200px] bg-background border border-border font-normal", triggerClassName)}
            style={{ maxWidth: "100%" }}
          >
            <span className={cn("truncate flex-1 text-left", isPlaceholder ? "text-muted-foreground" : "text-foreground")}>
              {displayValue}
            </span>
            <div className="flex items-center gap-1 ml-2 flex-shrink-0">
              {value && (
                <X
                  className="h-4 w-4 opacity-50 hover:opacity-100 cursor-pointer"
                  onClick={handleClear}
                />
              )}
              <ChevronDown className="h-4 w-4 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0"
          style={{ width: "200px", maxWidth: "90vw" }}
          align="start"
          sideOffset={4}
        >
          {enableSearch && (
            <div
              className="px-3 py-2 border-b bg-background sticky top-0 z-10"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => {
                if (e.target.tagName !== "INPUT") e.preventDefault();
              }}
            >
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  ref={inputRef}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={`Search ${label}...`}
                  className="pl-8 h-9 text-sm"
                  onKeyDown={(e) => {
                    if (["Escape", "ArrowUp", "ArrowDown", "Enter", " "].includes(e.key)) {
                      e.stopPropagation();
                    }
                  }}
                  autoComplete="off"
                />
              </div>
            </div>
          )}
          <div className="p-2">
            <div className={cn("space-y-1 overflow-auto max-h-[200px]")}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((opt) => {
                  const isSelected = value === opt.value;
                  return (
                    <div
                      key={opt.value}
                      onClick={() => handleSelect(opt.value)}
                      className={cn(
                        "flex items-center px-2 py-1.5 rounded cursor-pointer text-sm hover:bg-muted transition-colors",
                        isSelected && "bg-muted"
                      )}
                    >
                      <div
                        className={cn(
                          "h-4 w-4 mr-2 rounded border border-gray-400 flex-shrink-0 transition-colors flex items-center justify-center",
                          isSelected && "bg-primary border-primary"
                        )}
                      >
                        {isSelected && (
                          <Check className="h-3 w-3 text-primary-foreground" />
                        )}
                      </div>
                      <span className="flex-1 break-words" style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
                        {opt.label}
                      </span>
                      {isSelected && (
                        <Check className="ml-auto h-4 w-4 text-primary flex-shrink-0" />
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="px-2 py-1.5 text-sm text-muted-foreground">
                  {enableSearch && debouncedSearch ? "No results found" : "No options available"}
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
} 